// Guides — long-form, evergreen pillar content. Shared content model
// (see docs.js). Each guide targets a head term + supports related articles.

export const GUIDES = [
  {
    slug: "utm-tracking",
    category: "Guide",
    tone: "mint",
    eyebrow: "PILLAR · CAMPAIGN ATTRIBUTION",
    title: "The complete guide to UTM tracking",
    excerpt:
      "What UTM parameters are, the five tags that matter, naming conventions that scale, and how to read the results without drowning in spreadsheets.",
    readTime: "11 min",
    date: "2026-05-12",
    updated: "2026-06-03",
    seo: {
      title: "UTM Tracking: The Complete Guide (with examples)",
      description:
        "Learn UTM parameters end to end — the 5 tags, a naming convention that scales, common mistakes, and how to track campaigns with short links.",
      keywords: [
        "utm tracking",
        "utm parameters",
        "utm builder",
        "campaign tracking",
        "utm best practices",
      ],
    },
    sections: [
      {
        type: "intro",
        body: "UTM parameters are the small tags you append to a URL so your analytics can tell exactly where a click came from. Get them consistent and every campaign becomes measurable; get them sloppy and your reports fracture into dozens of near-duplicate sources. This guide covers the five UTM tags, a naming convention that survives a growing team, and how to wire UTMs into short links so attribution is automatic.",
      },
      { type: "h2", title: "What is a UTM parameter?" },
      {
        type: "p",
        body: "A UTM (Urchin Tracking Module) parameter is a key-value pair added to a URL's query string. When someone clicks the tagged link, the parameters travel with them and are recorded by your analytics, telling you which campaign, channel, and creative drove the visit. They don't change where the link goes — only how the visit is labelled.",
      },
      { type: "h3", title: "The five UTM tags" },
      {
        type: "kv",
        pairs: [
          ["utm_source", "Where the traffic comes from — e.g. newsletter, twitter, google."],
          ["utm_medium", "The marketing channel — e.g. email, social, cpc, referral."],
          ["utm_campaign", "The specific campaign — e.g. spring_launch, black_friday."],
          ["utm_term", "Optional. The paid keyword for search campaigns."],
          ["utm_content", "Optional. Distinguishes creatives — e.g. hero_cta vs footer_link."],
        ],
      },
      {
        type: "code",
        body: "https://example.com/pricing\n  ?utm_source=newsletter\n  &utm_medium=email\n  &utm_campaign=spring_launch\n  &utm_content=header_button",
      },
      { type: "h2", title: "A naming convention that scales" },
      {
        type: "ol",
        items: [
          "Lowercase everything. utm_source=Twitter and utm_source=twitter are two different sources to your analytics.",
          "Pick one word per concept and document it. Decide between social and Social, email and newsletter — then never switch.",
          "Use underscores, not spaces. spring_launch, not spring%20launch.",
          "Use source = the platform, medium = the channel type. twitter/social, not twitter/twitter.",
          "Reserve utm_content for A/B-ish distinctions you'll actually report on.",
        ],
      },
      {
        type: "callout",
        tone: "yellow",
        title: "The #1 UTM mistake",
        body: "Inconsistent casing and synonyms. 'FB', 'facebook', and 'Facebook' split one channel into three rows. A shared builder or a link tool that stores UTMs per-link eliminates this at the source.",
      },
      { type: "h2", title: "How short links make UTMs reliable" },
      {
        type: "p",
        body: "Raw UTM-laden URLs are long, ugly, and easy to mistype when pasted across channels. With LinkLane, you attach UTM values to a short link once; every click appends them automatically and consistently. You share a clean lny.me/spring link, and the analytics still receive the full source/medium/campaign breakdown.",
      },
      {
        type: "feature-grid",
        items: [
          {
            title: "Set UTMs per link",
            body: "Store source, medium, campaign, term, and content on the link itself — no manual query strings.",
            tone: "mint",
          },
          {
            title: "Group with campaigns",
            body: "Assign links to a campaign and read a single rolled-up report across every link in it.",
            tone: "candy",
          },
          {
            title: "Read the breakdown",
            body: "Real-time analytics split clicks by referrer, device, country, and browser — bots filtered out.",
            tone: "pink",
          },
        ],
      },
      { type: "h2", title: "Reading the results" },
      {
        type: "p",
        body: "Once links are tagged, your campaign report answers three questions: which source drove the most qualified clicks, which medium converted, and which creative (utm_content) won. In LinkLane, the campaign view aggregates every link's clicks, uniques, and breakdowns so you compare channels without exporting to a spreadsheet — though a one-click CSV export is there when you want it.",
      },
      {
        type: "faq",
        title: "UTM tracking FAQ",
        items: [
          {
            q: "Do UTM parameters affect SEO?",
            a: "On your own links, avoid indexing UTM-tagged URLs (use canonical tags). For outbound campaign links, UTMs are standard and have no negative SEO effect.",
          },
          {
            q: "What's the difference between source and medium?",
            a: "Source is the specific origin (e.g. 'newsletter', 'twitter'); medium is the channel category (e.g. 'email', 'social'). Source answers 'who', medium answers 'what kind of channel'.",
          },
          {
            q: "How many UTM parameters should I use?",
            a: "Always set source, medium, and campaign. Add term for paid search keywords and content when you're testing creatives. The five standard tags are the only ones analytics tools special-case.",
          },
        ],
      },
      {
        type: "cta",
        eyebrow: "TRY IT",
        title: "Tag once, track everywhere.",
        to: "/signup",
        label: "Start free",
      },
    ],
    faq: [
      {
        q: "What is a UTM parameter?",
        a: "A tag added to a URL's query string (utm_source, utm_medium, utm_campaign, utm_term, utm_content) that tells analytics where a click came from, without changing the destination.",
      },
      {
        q: "Can I add UTMs to a short link?",
        a: "Yes. LinkLane stores UTM values on the link itself and appends them automatically on every click, so you share a clean short URL while analytics still receive full attribution.",
      },
      {
        q: "What are the five UTM parameters?",
        a: "utm_source, utm_medium, utm_campaign, utm_term, and utm_content. The first three are essential; term and content are optional.",
      },
    ],
    related: ["compare:linklane-vs-bitly", "use-cases:agencies", "guides:link-in-bio-strategy"],
  },

  {
    slug: "link-in-bio-strategy",
    category: "Guide",
    tone: "pink",
    eyebrow: "PILLAR · CREATOR GROWTH",
    title: "Link-in-bio strategy for creators",
    excerpt:
      "Your bio link is the most valuable real estate you own. Here's how to structure it, theme it on-brand, and track which links actually drive your goals.",
    readTime: "9 min",
    date: "2026-05-08",
    updated: "2026-06-03",
    seo: {
      title: "Link-in-Bio Strategy: One Link, Real Results",
      description:
        "A practical link-in-bio playbook: how to order links, theme your page on-brand, drive your priority action, and track every tap with analytics.",
      keywords: [
        "link in bio",
        "link in bio strategy",
        "bio page",
        "linktree alternative",
        "instagram bio link",
      ],
    },
    sections: [
      {
        type: "intro",
        body: "On most platforms you get exactly one clickable link. Your bio page is where that single link has to do the work of a homepage, a store, and a press kit at once. A good bio page isn't a link dump — it's a prioritized, on-brand funnel that points people at the one or two things you actually want them to do.",
      },
      { type: "h2", title: "Order links by intent, not recency" },
      {
        type: "p",
        body: "The top slot gets the most taps by a wide margin. Put your current priority there — the new release, the waitlist, the product — not whatever you added last. Everything below should descend in importance. Drag-reorder makes this a ten-second habit rather than a rebuild.",
      },
      { type: "h2", title: "Make it unmistakably yours" },
      {
        type: "p",
        body: "A bio page that matches your brand converts better than a generic one. LinkLane's Theme Studio lets you pick from a set of themes and tune colors, fonts, and button style so the page feels like an extension of your feed — not a third-party detour. Add your avatar and a one-line bio so visitors know they're in the right place.",
      },
      {
        type: "feature-grid",
        items: [
          {
            title: "Drag to reorder",
            body: "Reprioritize links in seconds as campaigns change.",
            tone: "pink",
          },
          {
            title: "On-brand themes",
            body: "Theme Studio: colors, fonts, button styles, backgrounds.",
            tone: "candy",
          },
          {
            title: "Live mobile preview",
            body: "See exactly what followers see while you edit.",
            tone: "mint",
          },
          {
            title: "Unified analytics",
            body: "Every tap flows into the same analytics as your short links.",
            tone: "yellow",
          },
        ],
      },
      { type: "h2", title: "Track what actually works" },
      {
        type: "p",
        body: "Most bio tools tell you nothing. The point of a bio page is to learn which link drives your goal — so you can promote the winners and cut the dead weight. With LinkLane, per-link taps and page views feed the same real-time analytics as the rest of your links, with device and country breakdowns and bot filtering.",
      },
      {
        type: "callout",
        tone: "candy",
        title: "Claim a clean handle",
        body: "A memorable slug (yourname) is easier to say on camera and type than a random string. Grab yours before someone else does.",
      },
      {
        type: "faq",
        title: "Link-in-bio FAQ",
        items: [
          {
            q: "How many links should a bio page have?",
            a: "Fewer than you think. Lead with one or two priority links; keep the total short enough that the top item still gets attention. Reorder anytime as priorities shift.",
          },
          {
            q: "Can I match my bio page to my brand?",
            a: "Yes — LinkLane's Theme Studio lets you customize colors, fonts, button styles, and background, plus your avatar and bio text, with a live mobile preview.",
          },
          {
            q: "Do bio page clicks show up in analytics?",
            a: "Yes. Page views and per-link taps feed the same real-time analytics as your short links, including device and country breakdowns.",
          },
        ],
      },
      {
        type: "cta",
        eyebrow: "CLAIM IT",
        title: "One link. Your whole brand.",
        to: "/signup",
        label: "Build your bio page",
      },
    ],
    faq: [
      {
        q: "What is a link-in-bio page?",
        a: "A single shareable page that holds all your important links, used where a platform allows only one clickable link (like Instagram or TikTok bios).",
      },
      {
        q: "Is LinkLane a Linktree alternative?",
        a: "Yes — LinkLane includes themeable bio pages plus short links, branded QR, smart routing, and real-time analytics in one platform.",
      },
    ],
    related: [
      "compare:linklane-vs-linktree",
      "alternatives:best-linktree-alternatives",
      "use-cases:creators",
    ],
  },

  {
    slug: "qr-codes-for-marketing",
    category: "Guide",
    tone: "candy",
    eyebrow: "PILLAR · OFFLINE TO ONLINE",
    title: "QR codes for marketing that actually convert",
    excerpt:
      "How to design branded QR codes people trust, where to place them, and — most importantly — how to measure whether they worked.",
    readTime: "8 min",
    date: "2026-05-02",
    updated: "2026-06-03",
    seo: {
      title: "QR Codes for Marketing: Design & Tracking",
      description:
        "A practical guide to QR codes that convert: branded design, smart placement, error-correction tips, and how to track every scan as a click.",
      keywords: [
        "qr code marketing",
        "branded qr code",
        "qr code analytics",
        "trackable qr code",
        "qr code generator",
      ],
    },
    sections: [
      {
        type: "intro",
        body: "A QR code is the bridge between something physical — a poster, a package, a table tent — and a web destination you control. The problem with most QR codes is that they're anonymous: a black-and-white square you can never change and never measure. Branded, trackable QR codes fix both.",
      },
      { type: "h2", title: "Design a QR people trust" },
      {
        type: "list",
        items: [
          "Brand it. Add your logo to the center and use your colors — a styled code reads as legitimate, a naked one reads as spam.",
          "Keep contrast high. Dark foreground on light background scans fastest; avoid low-contrast color pairs.",
          "Raise error correction when you add a logo, so the code still scans if the center is partly obscured.",
          "Test it at the size you'll print it — a sticker and a billboard have very different minimums.",
        ],
      },
      { type: "h2", title: "Make every scan measurable" },
      {
        type: "p",
        body: "Because a LinkLane QR points at a short link, a scan is just a click — with a source of 'qr'. That means scans land in the same analytics as everything else: counts, uniques, device, country, and time-of-day, all in real time. You finally know whether the conference banner or the product insert actually drove traffic.",
      },
      {
        type: "stat",
        items: [
          { value: "SVG + PNG", label: "Print-ready export" },
          { value: "Logo + frame", label: "Branding options" },
          { value: "Per-scan", label: "Click tracking" },
          { value: "Real-time", label: "Scan analytics" },
        ],
      },
      {
        type: "callout",
        tone: "mint",
        title: "Point QR codes at editable links",
        body: "Because the QR encodes a short link, you can change the destination after it's printed. Update the link, and every existing code now points somewhere new — no reprint.",
      },
      {
        type: "faq",
        title: "QR marketing FAQ",
        items: [
          {
            q: "Can I track QR code scans?",
            a: "Yes. A LinkLane QR encodes a short link, so each scan is recorded as a click with a 'qr' source — visible in real-time analytics with device, country, and time breakdowns.",
          },
          {
            q: "Can I change a QR code's destination after printing?",
            a: "Yes, as long as it points at a short link. Update the link's destination and every printed code now resolves to the new URL — no reprint needed.",
          },
          {
            q: "What file formats can I export?",
            a: "Branded QR codes export as SVG (vector, ideal for print) and PNG.",
          },
        ],
      },
      {
        type: "cta",
        eyebrow: "PRINT IT",
        title: "Branded QR. Real scans.",
        to: "/signup",
        label: "Make a QR code",
      },
    ],
    faq: [
      {
        q: "Are QR code scans tracked as clicks?",
        a: "Yes — a LinkLane QR points at a short link, so each scan is a click tagged with a 'qr' source and appears in real-time analytics.",
      },
      {
        q: "Can I add my logo to a QR code?",
        a: "Yes. Branded QR codes (Pro) support a center logo, custom colors, frames, and SVG/PNG export.",
      },
    ],
    related: ["use-cases:events", "guides:utm-tracking", "compare:linklane-vs-bitly"],
  },
];

export const GUIDES_BY_SLUG = Object.fromEntries(GUIDES.map((e) => [e.slug, e]));
